import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { shedService } from '../services/shedService';
import { dealerService } from '../services/dealerService';

const EmbedInventory = () => {
  const { dealerId } = useParams();
  const [searchParams] = useSearchParams();
  const [sheds, setSheds] = useState([]);
  const [dealer, setDealer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Customization via query params
  const accentColor = searchParams.get('accent') || '#059669';
  const cols = parseInt(searchParams.get('cols')) || 3;
  const limit = parseInt(searchParams.get('limit')) || 50;
  const showPrice = searchParams.get('hidePrice') !== 'true';
  const showBranding = searchParams.get('hideBranding') !== 'true';
  const theme = searchParams.get('theme') || 'light';

  const isDark = theme === 'dark';
  const siteUrl = window.location.origin;

  useEffect(() => {
    if (dealerId) loadData();
  }, [dealerId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [shedsResult, dealerResult] = await Promise.all([
        shedService.getShedsByDealer(dealerId),
        dealerService.getDealerById(dealerId)
      ]);

      if (shedsResult.error && (!shedsResult.data || shedsResult.data.length === 0)) {
        setError('Unable to load inventory');
        return;
      }

      const available = (shedsResult.data || [])
        .filter(s => s.status === 'available')
        .slice(0, limit);
      setSheds(available);

      if (dealerResult.data) setDealer(dealerResult.data);
    } catch (err) {
      console.error('Embed load error:', err);
      setError('Unable to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getImageUrl = (shed) => {
    if (shed.images && shed.images.length > 0) return shed.images[0];
    return null;
  };

  const bgColor = isDark ? '#1a1a2e' : '#f9fafb';
  const cardBg = isDark ? '#16213e' : '#ffffff';
  const textPrimary = isDark ? '#e2e8f0' : '#111827';
  const textSecondary = isDark ? '#94a3b8' : '#6b7280';
  const borderColor = isDark ? '#334155' : '#e5e7eb';

  if (loading) {
    return (
      <div style={{
        minHeight: '200px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: bgColor,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '36px',
            height: '36px',
            border: `3px solid ${borderColor}`,
            borderTopColor: accentColor,
            borderRadius: '50%',
            animation: 'embedSpin 0.8s linear infinite',
            margin: '0 auto 12px'
          }} />
          <p style={{ color: textSecondary, fontSize: '14px', margin: 0 }}>Loading inventory...</p>
          <style>{`@keyframes embedSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '40px 20px',
        textAlign: 'center',
        background: bgColor,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <p style={{ color: textSecondary, fontSize: '14px' }}>{error}</p>
      </div>
    );
  }

  if (sheds.length === 0) {
    return (
      <div style={{
        padding: '40px 20px',
        textAlign: 'center',
        background: bgColor,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <p style={{ color: textSecondary, fontSize: '15px' }}>No sheds currently available. Check back soon!</p>
      </div>
    );
  }

  return (
    <div style={{
      background: bgColor,
      padding: '16px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      minHeight: '100vh'
    }}>
      {/* Dealer header */}
      {dealer && (
        <div style={{
          textAlign: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: `1px solid ${borderColor}`
        }}>
          <h2 style={{
            margin: '0 0 4px',
            fontSize: '20px',
            fontWeight: '700',
            color: textPrimary
          }}>
            {dealer.company_name}
          </h2>
          <p style={{
            margin: 0,
            fontSize: '13px',
            color: textSecondary
          }}>
            {sheds.length} shed{sheds.length !== 1 ? 's' : ''} available
          </p>
        </div>
      )}

      {/* Shed grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: '16px'
      }}>
        {sheds.map(shed => {
          const imgUrl = getImageUrl(shed);
          const hasDiscount = shed.onSale && shed.salePrice && shed.salePrice < shed.price;

          return (
            <a
              key={shed.id}
              href={`${siteUrl}/shed/${shed.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'block',
                background: cardBg,
                borderRadius: '10px',
                border: `1px solid ${borderColor}`,
                overflow: 'hidden',
                textDecoration: 'none',
                color: 'inherit',
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: 'pointer'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Image */}
              <div style={{
                position: 'relative',
                paddingTop: '66.67%',
                background: isDark ? '#0f172a' : '#f3f4f6',
                overflow: 'hidden'
              }}>
                {imgUrl ? (
                  <img
                    src={imgUrl}
                    alt={shed.name || 'Shed'}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    loading="lazy"
                  />
                ) : (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: textSecondary,
                    fontSize: '13px'
                  }}>
                    No Photo
                  </div>
                )}

                {/* Sale badge */}
                {hasDiscount && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    background: '#dc2626',
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: '700',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    letterSpacing: '0.5px'
                  }}>
                    SALE
                  </div>
                )}

                {/* Size badge */}
                {shed.size && (
                  <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    background: 'rgba(0,0,0,0.7)',
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: '600',
                    padding: '3px 8px',
                    borderRadius: '4px'
                  }}>
                    {shed.size}
                  </div>
                )}
              </div>

              {/* Card body */}
              <div style={{ padding: '12px 14px' }}>
                <h3 style={{
                  margin: '0 0 4px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: textPrimary,
                  lineHeight: '1.3',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {shed.name || `${shed.brand || ''} ${shed.model || ''}`.trim() || 'Shed'}
                </h3>

                {/* Specs */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                  marginBottom: '8px'
                }}>
                  {shed.shedType && (
                    <span style={{
                      fontSize: '11px',
                      color: textSecondary,
                      background: isDark ? '#1e293b' : '#f3f4f6',
                      padding: '2px 6px',
                      borderRadius: '3px'
                    }}>
                      {shed.shedType.charAt(0).toUpperCase() + shed.shedType.slice(1)}
                    </span>
                  )}
                  {shed.material && (
                    <span style={{
                      fontSize: '11px',
                      color: textSecondary,
                      background: isDark ? '#1e293b' : '#f3f4f6',
                      padding: '2px 6px',
                      borderRadius: '3px'
                    }}>
                      {shed.material}
                    </span>
                  )}
                  {shed.condition && (
                    <span style={{
                      fontSize: '11px',
                      color: textSecondary,
                      background: isDark ? '#1e293b' : '#f3f4f6',
                      padding: '2px 6px',
                      borderRadius: '3px'
                    }}>
                      {shed.condition}
                    </span>
                  )}
                </div>

                {/* Colors */}
                {(shed.bodyColor || shed.roofColor) && (
                  <p style={{
                    margin: '0 0 8px',
                    fontSize: '11px',
                    color: textSecondary
                  }}>
                    {[shed.bodyColor, shed.roofColor].filter(Boolean).join(' / ')}
                  </p>
                )}

                {/* Lot location */}
                {shed.lotLocation && (
                  <p style={{
                    margin: '0 0 8px',
                    fontSize: '11px',
                    color: textSecondary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <span style={{ fontSize: '12px' }}>📍</span>
                    {shed.lotLocation}
                  </p>
                )}

                {/* Price */}
                {showPrice && shed.price && (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    <span style={{
                      fontSize: '17px',
                      fontWeight: '700',
                      color: accentColor
                    }}>
                      {formatPrice(hasDiscount ? shed.salePrice : shed.price)}
                    </span>
                    {hasDiscount && (
                      <span style={{
                        fontSize: '13px',
                        color: textSecondary,
                        textDecoration: 'line-through'
                      }}>
                        {formatPrice(shed.price)}
                      </span>
                    )}
                  </div>
                )}

                {/* CTA */}
                <div style={{
                  marginTop: '10px',
                  background: accentColor,
                  color: '#fff',
                  textAlign: 'center',
                  padding: '8px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  letterSpacing: '0.3px'
                }}>
                  View Details
                </div>
              </div>
            </a>
          );
        })}
      </div>

      {/* Powered by ShedX */}
      {showBranding && (
        <div style={{
          textAlign: 'center',
          marginTop: '20px',
          paddingTop: '12px',
          borderTop: `1px solid ${borderColor}`
        }}>
          <a
            href={siteUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: '12px',
              color: textSecondary,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            Powered by <span style={{ fontWeight: '700', color: accentColor }}>ShedX</span>
          </a>
        </div>
      )}

      {/* Responsive overrides */}
      <style>{`
        @media (max-width: 768px) {
          div[style*="grid-template-columns"] {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 480px) {
          div[style*="grid-template-columns"] {
            grid-template-columns: 1fr !important;
          }
        }
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
};

export default EmbedInventory;
